import { type FC, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { Drawer, EmptyState, FilterBar, LoadingState, SearchInput, useToast } from '@/components/base';
import { type LegalSite, useLegalSites } from '@/hooks/useLegalSites';

type SiteFormState = {
  name: string;
  url: string;
  category: string;
  description: string;
  courtOrState: string;
  notes: string;
  isActive: boolean;
};

const categories = [
  'Tribunais',
  'Sistemas processuais',
  'Diários oficiais',
  'Órgãos públicos',
  'Financeiro',
  'Ferramentas internas',
  'Pesquisa jurídica',
  'Outros',
];

const emptyFormState: SiteFormState = {
  name: '',
  url: '',
  category: '',
  description: '',
  courtOrState: '',
  notes: '',
  isActive: true,
};

const siteToFormState = (site: LegalSite): SiteFormState => ({
  name: site.name,
  url: site.url,
  category: site.category ?? '',
  description: site.description ?? '',
  courtOrState: site.courtOrState ?? '',
  notes: site.notes ?? '',
  isActive: site.isActive,
});

const SitesPage: FC = () => {
  const { addToast } = useToast();
  const {
    legalSites,
    isLoadingLegalSites,
    legalSitesError,
    loadLegalSites,
    createLegalSite,
    updateLegalSite,
    archiveLegalSite,
  } = useLegalSites();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSite, setEditingSite] = useState<LegalSite | null>(null);
  const [selectedSite, setSelectedSite] = useState<LegalSite | null>(null);
  const [form, setForm] = useState<SiteFormState>(emptyFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [siteActionId, setSiteActionId] = useState<string | null>(null);

  const filteredSites = useMemo(() => {
    const term = search.trim().toLowerCase();
    return legalSites.filter((site) => {
      const matchesCategory = !categoryFilter || site.category === categoryFilter;
      const matchesSearch =
        !term ||
        [site.name, site.url, site.description, site.notes, site.courtOrState]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, legalSites, search]);

  const openCreateForm = () => {
    setEditingSite(null);
    setForm(emptyFormState);
    setShowForm(true);
  };

  const openEditForm = (site: LegalSite) => {
    setEditingSite(site);
    setForm(siteToFormState(site));
    setShowForm(true);
  };

  const closeForm = () => {
    if (isSaving) {
      return;
    }

    setShowForm(false);
    setEditingSite(null);
    setForm(emptyFormState);
  };

  const buildPayload = (): SiteFormState => ({
    name: form.name.trim(),
    url: form.url.trim(),
    category: form.category.trim(),
    description: form.description.trim(),
    courtOrState: form.courtOrState.trim(),
    notes: form.notes.trim(),
    isActive: form.isActive,
  });

  const handleSave = async () => {
    const payload = buildPayload();

    if (!payload.name) {
      addToast('O nome do site jurídico é obrigatório.', 'warning');
      return;
    }

    if (!payload.url) {
      addToast('A URL do site jurídico é obrigatória.', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      if (editingSite) {
        await updateLegalSite(editingSite.id, payload);
        addToast('Site jurídico atualizado com sucesso.', 'success');
      } else {
        await createLegalSite(payload);
        addToast('Site jurídico criado com sucesso.', 'success');
      }

      closeForm();
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível salvar o site jurídico.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (site: LegalSite) => {
    if (siteActionId) {
      return;
    }

    if (!window.confirm('Tem certeza que deseja arquivar este site jurídico? Esta ação removerá o link da listagem, mas manterá o registro no banco para auditoria.')) {
      return;
    }

    setSiteActionId(site.id);

    try {
      await archiveLegalSite(site.id);
      addToast('Site jurídico arquivado com sucesso.', 'success');
      if (selectedSite?.id === site.id) {
        setSelectedSite(null);
      }
    } catch (error: any) {
      addToast(error?.message || 'Não foi possível arquivar o site jurídico.', 'error');
    } finally {
      setSiteActionId(null);
    }
  };

  const openSite = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Sites Jurídicos</h2>
            <p className="text-sm text-foreground-500 mt-0.5">Favoritos úteis para consulta rápida e operação diária.</p>
          </div>
          <button
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i> Novo site
          </button>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nome, URL, descrição, observação ou órgão/estado..."
            className="w-full lg:max-w-xl"
          />
          <FilterBar
            filters={[
              {
                key: 'category',
                label: 'Categoria',
                value: categoryFilter,
                onChange: setCategoryFilter,
                options: categories.map((category) => ({ label: category, value: category })),
              },
            ]}
          />
        </div>

        {legalSitesError && (
          <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <i className="ri-alert-line"></i>
              <span>{legalSitesError}</span>
            </div>
            <button
              onClick={() => void loadLegalSites()}
              className="text-xs font-medium text-amber-700 hover:text-amber-800 cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {isLoadingLegalSites && legalSites.length === 0 ? (
          <LoadingState message="Carregando sites jurídicos..." />
        ) : filteredSites.length === 0 ? (
          <EmptyState
            icon="ri-links-line"
            title="Nenhum site cadastrado"
            description="Cadastre sites jurídicos úteis para abrir com rapidez durante o atendimento."
            action={{ label: 'Cadastrar site', onClick: openCreateForm, icon: 'ri-add-line' }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredSites.map((site) => (
              <div key={site.id} className="bg-background-50 border border-background-200 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{site.name}</h3>
                    <p className="text-xs text-foreground-500 break-all">{site.url}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
                    Ativo
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  {site.category && (
                    <div className="flex items-center gap-2 text-foreground-600">
                      <i className="ri-price-tag-3-line text-foreground-400"></i>
                      <span>{site.category}</span>
                    </div>
                  )}
                  {site.courtOrState && (
                    <div className="flex items-center gap-2 text-foreground-600">
                      <i className="ri-government-line text-foreground-400"></i>
                      <span>{site.courtOrState}</span>
                    </div>
                  )}
                  {site.description && <p className="text-xs text-foreground-500">{site.description}</p>}
                  {site.notes && <p className="text-xs text-foreground-500">{site.notes}</p>}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => openSite(site.url)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line mr-1"></i>Abrir
                  </button>
                  <button
                    onClick={() => openEditForm(site)}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1"></i>Editar
                  </button>
                  <button
                    onClick={() => void handleArchive(site)}
                    disabled={siteActionId === site.id}
                    className="text-xs px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {siteActionId === site.id ? 'Processando...' : 'Arquivar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Drawer
          isOpen={showForm}
          onClose={closeForm}
          title={editingSite ? 'Editar site jurídico' : 'Novo site jurídico'}
          width="w-[620px]"
          footer={
            <>
              <button
                onClick={closeForm}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => void handleSave()}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="Ex: Consulta Processual TJSP"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">URL *</label>
              <input
                type="url"
                value={form.url}
                onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                placeholder="https://www.tjsp.jus.br"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Categoria</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Selecione...</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Órgão / Estado</label>
                <input
                  type="text"
                  value={form.courtOrState}
                  onChange={(e) => setForm((prev) => ({ ...prev, courtOrState: e.target.value }))}
                  className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                  placeholder="Ex: TJSP / São Paulo"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                placeholder="Observação curta sobre o uso deste site"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Observações</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
                placeholder="Atalho, login interno, dica de uso, observação operacional..."
              />
            </div>
          </div>
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default SitesPage;
